import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useLocalization } from 'hooks/useLocalization';
import { useBusiness } from 'hooks/useBusiness';
import { formatNumberWithSpacesAndRounding } from 'utils/formatUtils';
import { Activity } from "interfaces/ActivityInterfaces";
import { ActivityInteractionData } from "interfaces/ActivityShopUIInterfaces";
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import { Box, Typography, Card, CardHeader, CardMedia, CardContent, CardActions } from '@mui/material';
import AlertFullWidth from 'components/shared/AlertFullWidth';


import { BoxProps } from '@mui/material/Box';
import { SxProps, Theme } from '@mui/system';


import {
	Hiking as HikingIcon
} from '@mui/icons-material';

import { motion } from 'framer-motion';

import useCleanText from 'hooks/useCleanText';
import ActivityDuration from 'components/activity-display/activity-content/ActivityDuration';
import ActivityGridItemQuickInfoBar from 'components/activity-display/layouts/activity-grid/ActivityGridItemQuickInfoBar';
import ActivityGridItemBottomBarContent from 'components/activity-display/layouts/activity-grid/ActivityGridItemBottomBarContent';

//Import ActivityTile.css
import './ActivityTile.css';

interface ActivityTilesTileProps {
	activity: Activity;
}

const MotionCard = motion(Card);

function ActivityCard({ activity }: ActivityTilesTileProps) {
	const [isHovered, setIsHovered] = useState(false);
	const { setSelectedActivityId, getActivityInteractionData, getTotalPaxQty, paxArr } = useActivityShopUI();
	const { getStr } = useLocalization();
	const cleanDescShort = useCleanText(activity.descShort);

	const coverUrl = activity.images.main.url ? activity.images.main.url : (
		activity.images.gallery.length > 0 && activity.images.gallery[0].url ? activity.images.gallery[0].url : null
	);

	function handleClick() {
		setSelectedActivityId(activity.id);
	}
	
	const totalPaxQty = getTotalPaxQty(paxArr);

	const activityInteractionData = getActivityInteractionData(activity.id);
	if (!activityInteractionData) return null;
	
	return (
        <Link to={`/${getStr('PUBLIC_APP_URL_info')}/${activity.id}/${activity.slug}`} onClick={handleClick} style={{ textDecoration: 'none' }}>
            <MotionCard
                sx={{
                    display: 'flex',  
                    flexDirection: 'column',  
                    height: '100%',
                }}
                whileHover={{ scale: 1.03 }}
            >
                <CardHeader
					title={activity.title ? activity.title : getStr('Name_unset')}
					subheader={
						<ActivityGridItemQuickInfoBar activity={activity} />
					}
					titleTypographyProps={{
					  sx: !activity.title
						? { color: 'red', textTransform: 'uppercase' }
						: {},
					}}
				/>

				{coverUrl === null ? (

					<RatioBox x={3} y={2} centered sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
						<HikingIcon sx={{ fontSize: 120, color: 'rgba(0, 0, 0, 0.15)' }} />
					</RatioBox>
				) : (
					<div
						style={{
							height: 0,
							paddingTop: '66.67%', 
							backgroundSize: 'cover',
							backgroundImage: `url(${coverUrl})`,
						}}
					/>
				)

				}
				<div style={{ flexGrow: 1 }}>
					{
						cleanDescShort ? (
							<CardContent>
								<Typography sx={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: '-webkit-box',
									WebkitLineClamp: 5,
									WebkitBoxOrient: 'vertical',
									whiteSpace: 'normal'
								}}>
									{cleanDescShort}
								</Typography>
							</CardContent>
						) : null
					}
				</div>
                <CardActions sx={{ paddingTop: '8px', paddingBottom: '8px' }}>
					<ActivityGridItemBottomBarContent activity={activity} isHovered={isHovered} totalPaxQty={totalPaxQty} activityInteractionData={activityInteractionData} />
				</CardActions>
            </MotionCard>
        </Link>
	);
}

function ActivityTileContentPadding({ children }: { children: React.ReactNode }) {
	return (
		<Box sx={{
			padding: '10px 20px'
		}}>
			{children}
		</Box>
	);
}


interface RatioBoxProps extends BoxProps {
	x: number;
	y: number;
	sx?: SxProps<Theme>;
	children: React.ReactNode;
	centered?: boolean;
  }
  
  const RatioBox: React.FC<RatioBoxProps> = ({ x, y, sx, children, centered }) => {
	const paddingTop = `${(y / x) * 100}%`;
  
	return (
	  <Box
		display="flex"
		alignItems="center"
		justifyContent="center"
		sx={{
		  position: 'relative',
		  width: '100%', // or any specific width you want
		  '&::before': {
			content: '""',
			display: 'block',
			paddingTop,
		  },
		  ...sx,
		}}
	  >
		<Box
		  sx={{
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			display: 'flex',
			alignItems: centered ? 'center' : 'flex-start',
			justifyContent: centered ? 'center' : 'flex-start',
		  }}
		>
		  {children}
		</Box>
	  </Box>
	);
  };

export default ActivityCard;